import { IIconProps, IPersonaStyles } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPIconButton, IPScrollablePane, IPSpinner, IPText, IPTooltipHost } from 'baseComponents';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { SkypeCircleCheckIcon } from 'components/Checklist/CheckListStyles';
import LinkConfirmationModal from 'components/Links/Preview/LinkConfirmationModal';
import { LinkPreviewModalOrUrl } from 'components/Links/Preview/LinkPreviewModalOrUrl';
import SingleTask from 'components/Tasks/SingleTask';
import InfiniteList from 'components/Utils/InifiniteList';
import DateDividerListItem, {
  DataGroup,
  DataGroupComparer,
  DataGroupGetName,
  DataGroupListItem,
  DateComparer,
  DateDivider,
  insertDividersAndGroupsInList,
} from 'components/Utils/ListItemDateDividers';
import { collapseIcon, globalStackItemStylesScroll, globalTextStylesBold } from 'globalStyles';
import Activity, { ActivityType } from 'models/activity';
import EntityDTO from 'models/dto/entityDTO';
import { EntityTypes } from 'models/entity';
import ResourceLink from 'models/resourceLink';
import Task from 'models/tasks/task';
import { IWidgetCustomAction } from 'models/widget';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  apiDismissActivity,
  apiDismissAllActivities,
  apiGetActivitiesForDashboard,
} from 'services/Api/dashboardService';
import { apiGetLink } from 'services/Api/linkService';
import { apiGetSingleTask } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { toLocaleDateShortNoYear, toLocaleTimeNoSeconds } from 'utils/datetime';
import { sortOnDate } from 'utils/sorting';
import { getRandomId, toNameInitial } from 'utils/string';
import { navigateToExternalUrl } from 'utils/url';
import { IWidgetRendererProps } from '../WidgetRenderer';

interface IWidgetMyActionsProps extends IWidgetRendererProps {}

const WidgetMyActions: FunctionComponent<IWidgetMyActionsProps> = (props: IWidgetMyActionsProps) => {
  const { t } = useTranslation(['translation', 'dashboard']);
  const appContext = useContext(AppContext);
  const history = useHistory();
  const [actionData, setActionData] = useState<Activity[] | undefined>(undefined);
  const [actionDataDisplay, setActionDataDisplay] = useState<
    (Activity | DateDivider | DataGroup<Activity>)[] | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTask, setSelectedTask] = useState<Task | undefined>(undefined);
  const [selectedTaskInitialPivot, setSelectedTaskInitialPivot] = useState<string | undefined>(undefined);
  const [selectedLink, setSelectedLink] = useState<ResourceLink | undefined>(undefined);
  const [showIcon, setShowIcon] = useState<number | undefined>(undefined);
  const [hasMoreData, setHasMoreData] = useState<boolean>(true);
  const [expandedSignatures, setEpandedSignatures] = useState<Activity[]>([]);
  const [reRender, setReRender] = useState<string>('');
  const [showConfirmationModal, setShowConfirmationModal] = useState<number | undefined>(undefined);

  const dismissIcon: IIconProps = { iconName: 'RemoveFilter' };
  const colors: string[] = ['lightblue', 'blue', 'green', 'purple', 'red'];
  const actionTypeList = [
    ActivityType.OverDeadline,
    ActivityType.Refused,
    ActivityType.RefusedInstance,
    ActivityType.Assigned,
    ActivityType.Upcoming,
    ActivityType.FollowUpPending,
    ActivityType.LibraryNewVersionOwner,
    ActivityType.LinkedControlUpdated,
    ActivityType.LibraryReadRequest,
    ActivityType.AuditCriteriaChanged,
  ];

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget, props.sharedData]);

  const loadData = async (refresh?: boolean) => {
    try {
      if (props.onRefreshSharedData && props.widget.dashboard && refresh) {
        await props.onRefreshSharedData();

        return;
      }

      if (isLoading) return;
      setIsLoading(true);
      setHasMoreData(true);
      setEpandedSignatures([]);

      const activities = props.sharedData?.activities;

      setActionData(
        activities
          ?.filter((activity: Activity) => actionTypeList.includes(activity.typeOfActivity))
          .sort((a, b) => -1 * sortOnDate(a.created, b.created)),
      );
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (props.onRegisterCustomActions) {
      props.onRegisterCustomActions(props.widget, getCustomActions());
    }
    if (actionData && actionData?.length > 0) {
      const newData = insertDividersAndGroupsInList(actionData, getCompareDate, compareItemsForGroup, getGroupName);
      setActionDataDisplay(newData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionData, expandedSignatures]);

  const getCompareDate: DateComparer<Activity> = (a) => {
    return a.created;
  };

  const getGroupName: DataGroupGetName<Activity> = (a) => {
    return a.getPrimaryText(appContext.globalDataCache);
  };

  const compareItemsForGroup: DataGroupComparer<Activity> = (a, b) => {
    return a.typeOfActivity === b.typeOfActivity && !expandedSignatures.some((e) => e.activityId === a.activityId);
  };

  const getCustomActions = () => {
    const customActions: IWidgetCustomAction[] = [
      {
        name: t('widgets:MyActions.CustomActions.CollapseGroups'),
        iconProps: collapseIcon,
        disabled: !actionData || actionData?.length === 0,
        callback: collapseAll,
      },
      {
        name: t('widgets:MyActions.CustomActions.DismissAll'),
        iconProps: dismissIcon,
        disabled: !actionData || actionData?.length === 0,
        callback: dismissAll,
      },
    ];

    return customActions;
  };

  const collapseAll = async () => {
    setEpandedSignatures([]);
  };

  const dismissAll = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiDismissAllActivities(actionTypeList, accessToken);
      setActionData([]);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMoreData = async () => {
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const newActivities = await apiGetActivitiesForDashboard(
        actionTypeList,
        actionData?.length ?? 0,
        50,
        accessToken,
      );

      if (newActivities.length === 0) {
        setHasMoreData(false);
        //force last re-render of whole list, otherwise InfiniteList keeps sort of hanging on the last render page
        setActionData([...(actionData ?? [])]);
      } else {
        const newData = [...(actionData ?? [])];
        //add activities that not exist yet
        //for failsafe when fetch more returns the same data
        for (let idx = 0; idx < newActivities.length; idx++) {
          if (!newData?.some((a) => a.activityId === newActivities[idx].activityId)) {
            newData.push(newActivities[idx]);
          }
        }
        newData.sort((a, b) => -1 * sortOnDate(a.created, b.created));
        setActionData(newData);
      }
    } catch (err) {
      appContext.setError(err);
    }
  };

  //dismissing the modal is close's job: SingleTask also saves WITHOUT closing (a task type pane that
  //needs the stored task first), and clearing the selection here unmounts it in the middle of that
  const saveTask = async (task: Task, isNew: boolean, isCancelled: boolean) => {
    if (!isCancelled) {
      await loadData(true);
    }
  };

  const removeTask = async (task: Task) => {
    setSelectedTask(undefined);
    await loadData(true);
  };

  const updateTask = async (task: Task) => {};

  const getTaskDetail = async (taskId: number) => {
    if (!taskId) {
      return;
    }

    try {
      appContext.showContentLoader();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const tasks = await apiGetSingleTask(taskId, false, accessToken, appContext.globalDataCache);
      if (tasks && tasks.tasks && tasks.tasks.length > 0) {
        const task = tasks.tasks[0];
        setSelectedTask(task);
      } else {
        appContext.showNotification(t('dashboard:Errors.ItemHasBeenDeleted'), true);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
    }
  };

  const getLinkDetail = async (linkId: number) => {
    if (!linkId) {
      return;
    }

    try {
      appContext.showContentLoader();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const link = await apiGetLink(linkId, accessToken, appContext.globalDataCache);
      if (link) {
        setSelectedLink(link);
      } else {
        appContext.showNotification(t('dashboard:Errors.ItemHasBeenDeleted'), true);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
    }
  };

  const onItemClickHandler = (activity: Activity) => {
    switch (activity.entity.typeOfEntity) {
      case EntityTypes.Requirement:
        history.push(`/theme/${activity.entity.entityId}`);
        break;
      case EntityTypes.Control:
        if (activity.typeOfActivity === ActivityType.LinkedControlUpdated && activity.refData) {
          const entity: EntityDTO = JSON.parse(activity.refData);
          if (entity && entity.entityId && entity.entityOrgUnitId) {
            navigateToExternalUrl(
              `/control/${entity.entityId}`,
              appContext.user.tenant,
              entity.entityOrgUnitId,
            );
          }
        } else {
          history.push(`/control/${activity.entity.entityId}`);
        }
        break;
      case EntityTypes.Task:
        setSelectedTaskInitialPivot(
          activity.typeOfActivity === ActivityType.FollowUpPending ? 'Context' : undefined,
        );
        getTaskDetail(activity.entity.entityId);
        break;
      case EntityTypes.Risk:
        history.push(`/risk/${activity.entity.entityId}`);
        break;
      case EntityTypes.Link:
        if (activity.typeOfActivity === ActivityType.LibraryReadRequest) {
          setShowConfirmationModal(activity.entity.entityId);
        } else {
          getLinkDetail(activity.entity.entityId);
        }
        break;
      default:
        break;
    }
  };

  const dismissActivity = async (activityId: number) => {
    try {
      appContext.showContentLoader();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiDismissActivity(activityId, accessToken);
      setActionData(actionData?.filter((a) => a.activityId !== activityId));
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
    }
  };

  const personaStyle: Partial<IPersonaStyles> = {
    root: {
      marginBottom: 5,
      selectors: {
        '&:hover': {
          cursor: 'pointer',
        },
      },
    },
  };

  const getTooltipContent = (activity: Activity): JSX.Element => {
    if (!activity.tooltipContent) {
      activity.tooltipContent = (
        <IPStack>
          <IPStack.Item>
            <IPText styles={globalTextStylesBold}>{activity.primaryText}</IPText>
          </IPStack.Item>
          <IPStack.Item>
            <IPText variant="small">{activity.secondaryText}</IPText>
          </IPStack.Item>
        </IPStack>
      );
    }

    return activity.tooltipContent;
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill styles={{ root: { paddingTop: 5 } }}>
      <IPStack.Item grow styles={globalStackItemStylesScroll}>
        {actionData?.length === 0 && (
          <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
            <IPIconButton iconProps={SkypeCircleCheckIcon} />
          </IPStack>
        )}
        {actionData && actionData.length > 0 && (
          <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
            <InfiniteList
              onFetchMore={fetchMoreData}
              hasMoreData={hasMoreData}
              items={actionDataDisplay}
              pageSize={7}
              listHeight={330}
              reRender={reRender}
              onRenderCell={(activity: Activity | DateDivider | DataGroup<Activity>) => {
                if (!activity) return null;

                if (activity instanceof DateDivider) {
                  return <DateDividerListItem {...activity} />;
                }

                if (activity instanceof DataGroup) {
                  return (
                    <DataGroupListItem
                      {...activity}
                      onExpand={(items) => {
                        setEpandedSignatures([...expandedSignatures, ...items]);
                      }}
                    />
                  );
                }

                if (!activity.primaryText) activity.primaryText = activity.getPrimaryText(appContext.globalDataCache);
                if (!activity.secondaryText)
                  activity.secondaryText = activity.getSecondaryText(appContext.globalDataCache);

                return (
                  <IPStack
                    key={activity.activityId}
                    horizontal
                    horizontalAlign="space-between"
                    verticalAlign="center"
                    onMouseOver={() => {
                      setShowIcon(activity.activityId);
                      setReRender(getRandomId());
                    }}
                    onMouseLeave={() => {
                      setShowIcon(undefined);
                      setReRender(getRandomId());
                    }}
                    styles={{ root: { paddingRight: 5 } }}
                  >
                    <IPStack.Item styles={{ root: { width: '85%' } }}>
                      <IPTooltipHost content={getTooltipContent(activity)}>
                        <IPPersona
                          styles={personaStyle}
                          size={IPPersonaSize.size40}
                          text={activity.primaryText}
                          secondaryText={activity.secondaryText}
                          imageInitials={toNameInitial(appContext.globalDataCache.users.get(activity.userId).name)}
                          initialsColor={colors[activity.priority]}
                          onClick={() => onItemClickHandler(activity)}
                          showOverflowTooltip={false}
                        />
                      </IPTooltipHost>
                    </IPStack.Item>
                    {!(showIcon === activity.activityId && activity.dismissed === undefined) && (
                      <IPStack.Item styles={{ root: { minWidth: 40 } }}>
                        <IPText block variant="small">
                          {toLocaleDateShortNoYear(activity.created)}
                        </IPText>
                        <IPText variant="small">{toLocaleTimeNoSeconds(activity.created)}</IPText>
                      </IPStack.Item>
                    )}
                    {showIcon === activity.activityId && activity.dismissed === undefined && (
                      <IPStack.Item>
                        <IPTooltipHost content={t('dashboard:ActivityDismiss.label')}>
                          <IPIconButton iconProps={dismissIcon} onClick={() => dismissActivity(activity.activityId)} />
                        </IPTooltipHost>
                      </IPStack.Item>
                    )}
                  </IPStack>
                );
              }}
            ></InfiniteList>
          </IPScrollablePane>
        )}
      </IPStack.Item>
      {actionData?.length === 0 && (
        <IPStack.Item>
          <IPText>{t('dashboard:Action.NoItems')}</IPText>
        </IPStack.Item>
      )}
      {selectedTask && (
        <SingleTask
          task={selectedTask}
          isOpen={true}
          close={() => {
            setSelectedTask(undefined);
            setSelectedTaskInitialPivot(undefined);
          }}
          onSave={saveTask}
          onRemove={removeTask}
          onUpdate={updateTask}
          initialPivot={selectedTaskInitialPivot}
        />
      )}
      {selectedLink && (
        <LinkPreviewModalOrUrl isOpen={true} onClose={() => setSelectedLink(undefined)} link={selectedLink} />
      )}
      {showConfirmationModal !== undefined && (
        <LinkConfirmationModal
          links={[]}
          selectedLinkId={showConfirmationModal}
          isOpen={true}
          onClose={() => {
            setShowConfirmationModal(undefined);
            loadData(true);
          }}
        />
      )}
    </IPStack>
  );
};

export default WidgetMyActions;
